import * as React from "react";
import {MAPBOX_TOKEN} from "../../config";
import Pin from "../../components/pin";
import MapGL, {Marker} from 'react-map-gl';
import {getStyleUrl} from "./utils";
import {MapPopup} from "./popup";
import {MeasurementLayer} from "../../components/measure-layer";
import {EditableLayer} from "../../components/editable-layers";
import useInterval from "../../hooks/useInterval";
import {ILayerState, IMapState, IViewPort} from "../../store/types";
import {IPopupInfo} from "../../utils/getPopupInfo";
import {DISPATCH_TYPES} from "./mapDispatchToProps";
import {IContactNode, IFeature_CONO} from "../../types";
import {Contact} from "../../../types";

export const newEntityProperties: {[key: string]: string} = {
    name: '.Unknown',
    membertype: '.Unknown',
    websiteurl: '.Unknown',
    address: '.Unknown',
    city: '.Unknown',
    state: '.Unknown',
    zip: '.Unknown',
    notes: '.none',
    instruc: '.none'
};

interface IProps {
    /* props */
    mapStyle: IMapState['mapStyle'];
    sources: any;
    styleName: string;
    viewport: IViewPort;
    isEditing: boolean;
    isEditingPopup: boolean;
    isCreatingNewEntity: boolean;
    isMeasuring: boolean;
    layerState: ILayerState;
    layers: string[];
    databaseLayerIds: IMapState['databaseLayerIds'];
    interactiveLayerIds: string[];
    measureFeatures: IFeature_CONO[];
    temporaryFeatures: IFeature_CONO[];
    userLocation: any;
    popupInfo?: IPopupInfo;
    /* callbacks */
    resize: () => void;
    closePopup: () => void;
    toggleEditing: () => void;
    requestMapStyle: (url: string, name: string) => void;
    requestGeometry: DISPATCH_TYPES['requestGeometry'];
    onViewportChange: (viewport: IViewPort) => void;
    handleClick: (event: Event) => void;
    handleContextMenu: (event: any) => void;
    handleMeasureUpdate: (event: Event) => void;
    beginEditGeometry: (event: Event) => void;
    bufferExistingFeature: (fullUid: string, editMode?: string) => void;
    resetTempFeature: (event?: Event) => void;
    panToFeature: (event: Event) => void;
    setTempFeatures: (event: Event) => void;

    createContact: (feature: IFeature_CONO, contactInfo: IContactNode) => void;
    createEntityContactLink: (feature: IFeature_CONO, contactInfo: IContactNode|Contact) => void;
    editContact: (feature: IFeature_CONO, contactInfo: IContactNode) => void;
    updateFeature: (popupInfo: IPopupInfo & {oldType: string}) => Promise<void>;
    deleteFeature: (id: string, type: string) => void;
    removeEntityContactLink: (feature: IFeature_CONO, contactInfo: IContactNode) => void;
    deleteLayer: DISPATCH_TYPES['deleteLayer'];
    createLayer: DISPATCH_TYPES['createLayer'];
    updateLayerName: DISPATCH_TYPES['updateLayerName'];
    toggleLayerVisibility: DISPATCH_TYPES['toggleLayerVisibility'];
}

export function Map (props: IProps) {
    const mapgl = React.useRef<any>(undefined);
    const [isLoaded, setIsLoaded] = React.useState(false);
    const [editableLayerIsEditing, setEditableLayerIsEditing] = React.useState(false);
    const {
        /* props */
        mapStyle, sources,
        styleName, viewport,
        isEditing, isCreatingNewEntity, isMeasuring,
        layerState, layers, databaseLayerIds, interactiveLayerIds,
        temporaryFeatures,
        userLocation,
        popupInfo,
        /* callbacks */
        resize, closePopup, toggleEditing,
        requestMapStyle, requestGeometry,
        onViewportChange, handleClick, handleContextMenu,
        beginEditGeometry, bufferExistingFeature, resetTempFeature, setTempFeatures,

        createContact, createEntityContactLink, editContact,
        updateFeature, deleteFeature, removeEntityContactLink,
    } = props;

    // this runs after mapRef was mounted
    React.useLayoutEffect(() => {
        const map = mapgl?.current?.getMap();
        // if (isLoaded) {
        /* add map listeners */
        window.addEventListener('resize', resize);
        map.scrollZoom.disable();
        // }
        if (!isLoaded && styleName) {
            requestMapStyle(getStyleUrl(styleName, MAPBOX_TOKEN), styleName);
        }

        return () => {
            /* remove map listeners */
            window.removeEventListener('resize', resize);
        }
    }, [styleName, resize, isLoaded, requestMapStyle, requestGeometry])

    const handleOnLoad = React.useCallback(() => {
        requestGeometry();
        setIsLoaded(true);
    }, [setIsLoaded, requestGeometry]);

    const handleRefresh = React.useCallback(() => {
        requestMapStyle(getStyleUrl(styleName, MAPBOX_TOKEN), styleName);
        requestGeometry();
    }, [requestGeometry, requestMapStyle, styleName])

    useInterval(() => {
        handleRefresh();
    }, 30000);

    const handleEditStart = React.useCallback(() => {
        setEditableLayerIsEditing(true);
    }, [setEditableLayerIsEditing]);

    const handleEditEnd = React.useCallback(() => {
        setEditableLayerIsEditing(false);
    }, [setEditableLayerIsEditing]);

    const handleViewportChange = React.useCallback((vp) => {
        if (editableLayerIsEditing) return;
        onViewportChange(vp);
    }, [onViewportChange, editableLayerIsEditing]);

    /** components && render **/

    return <div>
        <MapGL
            ref={mapgl}
            {...viewport}
            bearing={0}
            pitch={0}
            mapStyle={mapStyle}
            interactiveLayerIds={interactiveLayerIds}
            onViewportChange={handleViewportChange}
            mapboxApiAccessToken={MAPBOX_TOKEN}
            onClick={handleClick}
            onContextMenu={handleContextMenu}
            onLoad={handleOnLoad}>

            {userLocation &&
                <Marker
                    longitude={userLocation.coords.longitude}
                    latitude={userLocation.coords.latitude} >
                    <Pin size={12}/>
                </Marker>
            }

            {(isMeasuring) && <MeasurementLayer
                viewport={viewport}
            />}

            {(isEditing) && <EditableLayer
                viewport={viewport}
                // editMode={this.props.editMode}
                onEditStart={handleEditStart}
                onEditEnd={handleEditEnd}
                temporaryFeatures={temporaryFeatures}
                update={setTempFeatures}
                sources={sources}/>}

            <MapPopup
                popupInfo={popupInfo}
                isCreatingNewEntity={isCreatingNewEntity}
                isEditing={isEditing}
                layers={layers}
                layerState={layerState}
                databaseLayerIds={databaseLayerIds}
                handleContextMenu={handleContextMenu}
                beginEditGeometry={beginEditGeometry}
                bufferExistingFeature={bufferExistingFeature}
                resetTempFeature={resetTempFeature}
                closePopup={closePopup}
                toggleEditing={toggleEditing}
                createContact={createContact}
                createEntityContactLink={createEntityContactLink}
                editContact={editContact}
                updateFeature={updateFeature}
                deleteFeature={deleteFeature}
                removeEntityContactLink={removeEntityContactLink}
            />
        </MapGL>
    </div>;
}
